import * as ACTIONS from 'constant/action/article'
import * as SEARCH_ACTIONS from 'constant/action/search'
import * as LOADER_ACTIONS from 'constant/action/loader'
import * as PAGINATION from 'constant/pagination'
import * as TABLE from 'constant/table'
import * as NUMBER from 'constant/number'
import React from 'react'
import { createAction } from 'redux-actions'
import { buildCategory, filterResultsBuilder } from 'helper/create-category-object'
import _ from 'lodash'
import isDraft from 'helper/is-draft'
import buildFileMeta from 'helper/build-file-meta'
import buildImageFile from 'helper/build-image-file'
import isPublished from 'helper/is-published'
import buildDescriptionIframes from 'helper/build-description-iframes'
import * as FileSaver from 'file-saver'
import * as XLSX from 'xlsx'
import * as XLSX_CONSTANT from 'constant/xlsx'
import { formatData, getColWidths } from 'helper/format-export'
import { createTreeObject } from 'action/search'
import axios from 'axios'
import { notification } from 'antd'
import { InfoCircleTwoTone } from '@ant-design/icons'

const updateArticles = createAction(ACTIONS.UPDATE_ARTICLES)
const updatePreviewArticles = createAction(ACTIONS.UPDATE_PREVIEW_ARTICLES)
const updateLatestArticles = createAction(ACTIONS.UPDATE_LATEST_ARTICLES)
const updateArticle = createAction(ACTIONS.UPDATE_ARTICLE)
const updateLoading = createAction(LOADER_ACTIONS.UPDATE_LOADING)
const updatePagination = createAction(ACTIONS.UPDATE_ARTICLE_PAGINATION)
const updateExportData = createAction(ACTIONS.UPDATE_EXPORT_DATA)
const updateArticleSearchTerm = createAction(ACTIONS.UPDATE_ARTICLE_SEARCH_TERM)

const updateAttachments = createAction(ACTIONS.UPDATE_ATTACHMENTS)
const updateLoadingAttachments = createAction(ACTIONS.UPDATE_LOADING_ATTACHMENTS)
const updateAttachmentsPagination = createAction(ACTIONS.UPDATE_ATTACHMENT_PAGINATION)
const updateNewAttachmentFileList = createAction(ACTIONS.UPDATE_NEW_ATTACHMENT_FILE_LIST)
const updatePreviewCategory = createAction(ACTIONS.UPDATE_PREVIEW_CATEGORY)
const updateSubCategoryFilter = createAction(ACTIONS.UPDATE_SUB_CATEGORY_FILTER)

const updateTemplates = createAction(ACTIONS.UPDATE_TEMPLATES)
const updateArticleAuthors = createAction(ACTIONS.UPDATE_ARTICLE_AUTHORS)

const updateLoadingDescription = createAction(LOADER_ACTIONS.UPDATE_LOADING_DESCRIPTION)
const updateArticleSubmitting = createAction(LOADER_ACTIONS.UPDATE_ARTICLE_SUBMITTING)
const updateAttachmentSubmitting = createAction(LOADER_ACTIONS.UPDATE_ATTACHMENT_SUBMITTING)
const updateLoadingLatestArticles = createAction(LOADER_ACTIONS.UPDATE_LOADING_LATEST_ARTICLES)
const updateLoadingPreviewArticles = createAction(LOADER_ACTIONS.UPDATE_LOADING_PREVIEW_ARTICLES)

const updateTreeObject = createAction(SEARCH_ACTIONS.UPDATE_TREE_OBJECT)
const updateSelectedKeys = createAction(SEARCH_ACTIONS.UPDATE_SELECTED_KEYS)

export const updateArticleDescription = createAction(ACTIONS.UPDATE_ARTICLE_DESCRIPTION)

export const resetState = () => (dispatch, getState, { article }) => {
  try {
    dispatch(updateArticleDescription(''))
    dispatch(updateArticle({}))
  } catch (error) {
    console.error(error)
  }
}

export const resetLatestPosts = () => (dispatch, getState, { article }) => {
  dispatch(updateLatestArticles({}))
}

export const getArticles = (sort = PAGINATION.SORT.ARTICLES, direction = PAGINATION.DESCENDING, start = NUMBER.ZERO, pagination = false) => async (dispatch, getState, { article }) => {
  dispatch(resetState)
  dispatch(updateLoading(true))
  dispatch(updatePagination({
    direction,
    sort,
    start
  }))

  const {
    article: articleState
  } = getState()

  const articles = await article.getArticles(pagination, sort, direction, TABLE.PAGE_SIZE, _.isNumber(start) ? start : _.get(articleState, 'article.start'))
  const { categories, defaultSelectedKeys } = await buildCategory(articles)
  const treeObject = await createTreeObject(categories)
  dispatch(updateSelectedKeys(defaultSelectedKeys))
  dispatch(updateTreeObject(treeObject))
  dispatch(updateArticles(articles))
  dispatch(updateLoading(false))
}

export const filterResults = (filterValues, start = NUMBER.ZERO, sort = PAGINATION.SORT.ARTICLES, direction = PAGINATION.DESCENDING) => async (dispatch, getState, { article }) => {
  dispatch(updateLoading(true))
  const {
    article: articleState
  } = getState()

  dispatch(updatePagination({
    direction,
    sort,
    start
  }))

  const articles = await article.getArticles(false, sort, direction, TABLE.PAGE_SIZE, _.isNumber(start) ? start : _.get(articleState, 'article.start'))
  if (!_.isEmpty(filterValues)) {
    const chosenArticles = await filterResultsBuilder(articles, filterValues)

    articles.results = _.compact(chosenArticles)
  }
  dispatch(updateArticles(articles))
  dispatch(updateLoading(false))
}

export const getPreviewArticles = (categoryUuid, spread = false, start = NUMBER.ZERO, localLoad = false) => async (dispatch, getState, { article }) => {
  dispatch(updateLoadingPreviewArticles(!localLoad))

  const {
    article: { previewArticles, subCategoryFilterValue }
  } = getState()

  const filterBy = { categoryUuid, subCategoryUuid: subCategoryFilterValue }

  const articles = await (
    subCategoryFilterValue
      ? article.filterPreviewArticles(filterBy, PAGINATION.SORT.ARTICLES, PAGINATION.DESCENDING, TABLE.PAGE_SIZE, start)
      : article.getPreviewArticles(categoryUuid, PAGINATION.SORT.ARTICLES, PAGINATION.DESCENDING, TABLE.PAGE_SIZE, start)
  )

  if (spread) {
    articles.results = [...previewArticles.results, ...articles.results]
  }

  dispatch(updatePreviewArticles(articles))
  dispatch(updatePreviewCategory(categoryUuid))
  dispatch(updateLoadingPreviewArticles(false))
}

export const getLatestArticles = (siteUuid) => async (dispatch, getState, { article }) => {
  dispatch(resetLatestPosts)
  dispatch(updateLoadingLatestArticles(true))

  const articles = await article.getLatestArticles(siteUuid)

  dispatch(updateLatestArticles(articles))
  dispatch(updateLoadingLatestArticles(false))
}

export const getArticle = (articleUuid) => async (dispatch, getState, { article }) => {
  dispatch(updateLoading(true))
  if (articleUuid !== 'undefined') {
    const res = await article.getArticle(articleUuid)
    if (!res.response) {
      dispatch(updateArticle({ status: res.status }))
    } else {
      const { response, status } = res

      if (response.description) {
        const html = await article.getDescriptionHtml(response.description)

        dispatch(updateArticleDescription(html))
      }
      dispatch(updateArticle({ ...response, status }))
    }
  } else {
    window.location.assign(window.location.origin)
  }

  dispatch(updateLoading(false))
}

export const getArticleAttachments = (articleUuid, sort = PAGINATION.SORT.ATTACHMENTS, direction = PAGINATION.ACENDING, start, pagination = true) => async (dispatch, getState, { article }) => {
  dispatch(updateLoadingAttachments(true))
  dispatch(updateAttachmentsPagination({
    direction,
    sort,
    start
  }))
  const {
    article: articleState
  } = getState()
  if (articleUuid !== 'undefined') {
    const attachments = await article.getAttachments(sort, direction, TABLE.PAGE_SIZE, _.isNumber(start) ? start : _.get(articleState, 'article.attachments.start'), articleUuid, pagination)

    dispatch(updateAttachments({ [articleUuid]: attachments }))
  }
  dispatch(updateLoadingAttachments(false))
}

export const deleteArticle = (uuid) => async (dispatch, getState, { article }) => {
  await article.deleteArticle(uuid)
  const {
    article: {
      pagination: {
        sort,
        direction,
        start
      }
    }
  } = getState()
  dispatch(getArticles(false, sort, direction, start))
}

export const addNewArticle = (articleObject) => async (dispatch, getState, { article }) => {
  try {
    dispatch(updateArticleSubmitting(true))

    const articleIsDraft = isDraft(articleObject)

    articleObject.description_meta = buildDescriptionIframes(articleObject.description_meta)
    articleObject.image_meta = buildImageFile(articleObject.image_meta)

    const { file: imageFile, meta: imageMeta } = buildFileMeta(articleObject.image_meta, articleObject.title)

    const { file: descriptionFile, meta: descriptionMeta } = buildFileMeta(articleObject.description_meta, articleObject.title)

    const newArticle = {
      ...articleObject,
      description_meta: descriptionMeta,
      image_meta: imageMeta,
      is_draft: articleIsDraft,
      published: isPublished(articleIsDraft, articleObject.published)
    }

    const response = await article.addNewArticle(newArticle)

    if (imageFile) {
      await article.uploadFile(response.image_url, imageFile)
    }

    if (descriptionFile) {
      await article.uploadFile(response.description_url, descriptionFile)
    }

    const { article: { pagination: { sort, direction, start } } } = getState()

    dispatch(updateArticleSubmitting(false))
    dispatch(getArticles(false, sort, direction, start))
    dispatch(updateArticleDescription(''))

    return newArticle
  } catch (error) {
    console.error(error)
    dispatch(updateArticleSubmitting(false))
    return { error: true, message: error.message }
  }
}

export const editArticle = (articleObject) => async (dispatch, getState, { article }) => {
  try {
    dispatch(updateArticleSubmitting(true))
    const { article: { articleDescription, pagination: { sort, direction, start } } } = getState()

    articleObject.description_meta = buildDescriptionIframes(articleObject.description_meta, articleDescription)
    articleObject.image_meta = buildImageFile(articleObject.image_meta)

    const articleIsDraft = isDraft(articleObject)

    const { file: imageFile, meta: imageMeta } = buildFileMeta(articleObject.image_meta, articleObject.title)

    const { file: descriptionFile, meta: descriptionMeta } = buildFileMeta(articleObject.description_meta, articleObject.title)

    const editedArticle = {
      ...articleObject,
      description_meta: descriptionMeta,
      image_meta: imageMeta,
      is_draft: articleIsDraft,
      published: isPublished(articleIsDraft, articleObject.published)
    }

    const response = await article.editArticle(editedArticle)

    if (imageFile) {
      await article.uploadFile(response.image_url, imageFile)
    }

    if (descriptionFile) {
      await article.uploadFile(response.description_url, descriptionFile)
    }

    dispatch(updateArticleSubmitting(false))
    dispatch(getArticles(false, sort, direction, start))
    return editedArticle
  } catch (error) {
    console.error(error)
    dispatch(updateArticleSubmitting(false))
    return { error: true, message: error.message }
  }
}

export const searchArticles = (searchTerm, sort = PAGINATION.SORT.ARTICLES, direction = PAGINATION.DESCENDING, start = NUMBER.ZERO) => async (dispatch, getState, { article }) => {
  dispatch(updateArticleSearchTerm(searchTerm))

  if (!searchTerm) {
    return dispatch(getArticles())
  }

  dispatch(updateLoading(true))
  dispatch(updatePagination({
    direction,
    sort,
    start
  }))
  const {
    article: articleState
  } = getState()
  const response = await article.searchArticles(searchTerm, sort, direction, TABLE.PAGE_SIZE, _.isNumber(start) ? start : _.get(articleState, 'articles.start'))
  dispatch(updateArticles(response))
  dispatch(updateLoading(false))
}

export const deleteAttachment = (uuid) => async (dispatch, getState, { article }) => {
  const { article_uuid: articleUuid } = await article.deleteAttachment(uuid)

  const {
    article: {
      attachmentsPagination: {
        sort,
        direction,
        start
      }
    }
  } = getState()

  dispatch(getArticleAttachments(articleUuid, sort, direction, start))
}

export const publishArticle = (uuid) => async (dispatch, getState, { article }) => {
  await article.publishArticle(uuid)

  const {
    article: {
      pagination: {
        sort,
        direction,
        start
      }
    }
  } = getState()

  dispatch(getArticles(false, sort, direction, start))
}

export const pinArticle = (uuid) => async (dispatch, getState, { article }) => {
  await article.pinArticle(uuid)

  const {
    article: {
      pagination: {
        sort,
        direction,
        start
      }
    }
  } = getState()

  dispatch(getArticles(false, sort, direction, start))
}

export const downloadAttachment = (uuid, path, siteUuid) => async (dispatch, getState, { article }) => {
  try {
    dispatch(updateLoadingAttachments(true))
    const {
      auth: {
        user: {
          location_tracking: tracking
        }
      }
    } = getState()
    if (tracking) {
      const res = await axios.get('https://geolocation-db.com/json/')

      const {
        licence_signed_url: url,
        signed_url: signedUrl
      } = await article.downloadAttachment(uuid, path, siteUuid, res.data)

      window.location.assign(signedUrl)
      if (url) {
        download(url, 'licence.pdf')
        licenceDownloadNotification()
      }
    } else {
      const {
        licence_signed_url: url,
        signed_url: signedUrl
      } = await article.downloadAttachment(uuid, path, siteUuid)
      window.location.assign(signedUrl)

      if (url) {
        download(url, 'licence.pdf')
        licenceDownloadNotification()
      }
    }

    dispatch(updateLoadingAttachments(false))
    return true
  } catch (error) {
    return false
  }
}

export const downloadLicense = (siteUuid) => async (dispatch, getState, { article }) => {
  try {
    const { signed_url: url } = await article.downloadLicense(siteUuid)

    if (url) {
      download(url, 'licence.pdf')
      licenceDownloadNotification()
    }
  } catch (error) {
    console.error(error)
    return error
  }
}

const download = (url, filename) => {
  fetch(url).then(function (t) {
    return t.blob().then((b) => {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(b)
      a.setAttribute('download', filename)
      a.click()
    }
    )
  })
}

export const setNewAttachmentFileList = (fileList) => (dispatch, getState, { article }) => dispatch(updateNewAttachmentFileList(fileList))

export const createAttachment = (attachmentObject) => async (dispatch, getState, { article }) => {
  try {
    dispatch(updateAttachmentSubmitting(true))
    const { file: attachmentFile, meta: attachmentMeta } = buildFileMeta({ file: attachmentObject.image_meta }, null, attachmentObject.file_name)

    const newAttachment = {
      ...attachmentObject,
      file_meta: attachmentMeta
    }

    const { signed_url: signedUrl } = await article.uploadAttachment(newAttachment)
    await article.uploadAttachmentToS3(signedUrl, attachmentFile)

    dispatch(updateAttachmentSubmitting(false))
    dispatch(getArticleAttachments(attachmentObject.article_uuid))

    return attachmentObject
  } catch (error) {
    console.error(error)
    dispatch(updateAttachmentSubmitting(false))
    return false
  }
}

export const getArticleDescription = (uuid) => async (dispatch, getState, { article }) => {
  dispatch(updateLoadingDescription(true))
  dispatch(updateArticleDescription(''))
  const { hasDescription, url } = await article.getArticleDescription(uuid)
  if (hasDescription) {
    const html = await article.getDescriptionHtml(url)

    dispatch(updateArticleDescription(html))
  }

  dispatch(updateLoadingDescription(false))
}

export const editAttachment = (articleUuid, attachmentObject) => async (dispatch, getState, { article }) => {
  try {
    dispatch(updateAttachmentSubmitting(true))

    await article.editAttachment(attachmentObject)

    dispatch(getArticleAttachments(articleUuid))
    dispatch(updateAttachmentSubmitting(false))
    return attachmentObject
  } catch (error) {
    dispatch(updateAttachmentSubmitting(false))
    return false
  }
}

export const setFilterValue = ({ key }) => (dispatch, getState) => {
  const {
    article: { previewCategory }
  } = getState()

  dispatch(updateSubCategoryFilter(key))
  dispatch(getPreviewArticles(previewCategory))
}

export const resetFilter = () => (dispatch, getState) => {
  const {
    article: { previewCategory }
  } = getState()

  dispatch(updateSubCategoryFilter(''))
  dispatch(getPreviewArticles(previewCategory))
}

export const getExportData = () => async (dispatch, getState, { article }) => {
  dispatch(updateExportData([]))

  const {
    article: {
      articles: {
        full_count: fullCount
      }
    }
  } = getState()

  const numberOfCalls = Math.ceil(fullCount / PAGINATION.EXPORT_REQUEST_SIZE)

  for (let callNumber = 0; callNumber < numberOfCalls; callNumber++) {
    const { results } = await article.getArticles(false, PAGINATION.SORT.ARTICLES, PAGINATION.DESCENDING, TABLE.PAGE_SIZE, callNumber * PAGINATION.EXPORT_REQUEST_SIZE)

    const {
      article: {
        exportData
      }
    } = getState()

    dispatch(updateExportData([...exportData, ...results]))
  }
}

export const makeXlsx = () => (dispatch, getState, { article }) => {
  const {
    article: {
      exportData
    }
  } = getState()

  const wsData = formatData(exportData)
  const workSheet = XLSX.utils.json_to_sheet(wsData)
  workSheet[XLSX_CONSTANT.COLUMN_WIDTHS] = getColWidths(wsData)

  const workBook = {
    SheetNames: [XLSX_CONSTANT.SHEET_NAME],
    Sheets: {
      data: workSheet
    }
  }

  const excelBuffer = XLSX.write(workBook, {
    bookType: XLSX_CONSTANT.BOOK_TYPE,
    type: XLSX_CONSTANT.TYPE
  })

  const data = new Blob([excelBuffer], { type: XLSX_CONSTANT.FILE_TYPE })

  FileSaver.saveAs(data, XLSX_CONSTANT.FILE_NAME)
}

export const getTemplates = () => async (dispatch, getState, { article }) => {
  try {
    const response = await article.getTemplates()
    dispatch(updateTemplates(response))
  } catch (error) {
    console.error(error)
    return error
  }
}

export const editAuthor = (values) => async (dispatch, getState, { article }) => {
  try {
    dispatch(updateArticleSubmitting(true))
    const response = await article.editAuthor(values)
    dispatch(updateArticleSubmitting(false))
    return response
  } catch (error) {
    console.error(error)
    return error
  }
}

export const getPotentialAuthors = (articleObj) => async (dispatch, getState, { article }) => {
  try {
    const response = await article.getPotentialAuthors(articleObj)
    dispatch(updateArticleAuthors(response))
    return response
  } catch (error) {
    console.error(error)
    return error
  }
}

export const resetAuthor = () => (dispatch, getState, { article }) => {
  dispatch(updateArticleAuthors(null))
}

export const licenceDownloadNotification = () => {
  notification.open({
    bottom: 90,
    description:
      'Asset and Usage Licence Downloaded.',
    duration: NUMBER.ZERO,
    icon: <InfoCircleTwoTone twoToneColor='#7f2fd1' />,
    message: 'Download Notification',
    placement: 'bottomRight'
  })
}
